import React from "react";
import { useSelector } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import styles from "./SchoolFeeStatusCards.module.css";

/* Map card `variant` → CSS class on the outer wrapper.
 * Add a new key here + a matching CSS block to introduce a new status. */
const VARIANT_CLASS = {
  success: styles.cardSuccess, // Collected (green)
  danger: styles.cardDanger, // Not Paid (red)
  warning: styles.cardWarning, // Pending Approval (orange)
  info: styles.cardInfo, // spare (blue)
};

/* Single fee card (Collected / Not Paid / Pending Approval / ...).
 * All display fields are pre-formatted strings from the parent —
 * the component intentionally does NO currency math itself so the
 * backend's rupee shortening (Cr / L / K) shows exactly as sent. */
const FeeStatusCard = ({ card }) => {
  const {
    variant = "info",
    amount,
    statusLabel,
    studentsText,
    stats = [], // e.g. [{ label: "On Time", value: 648 }, { label: "Overdue", value: 1500 }]
    breakdown, // { left: {label,amount,percent}, right: {label,amount,percent} }
  } = card;

  const variantClass = VARIANT_CLASS[variant] || VARIANT_CLASS.info;

  return (
    <div className={`${styles.card} ${variantClass}`}>
      {/* Top row: amount + status pill */}
      <div className={styles.cardTop}>
        <span className={styles.amount}>{amount}</span>
        <span className={styles.statusLabel}>{statusLabel}</span>
      </div>

      {/* Sub row: students count + mini stats (On Time / Overdue etc.) */}
      <div className={styles.cardSub}>
        <span className={styles.studentsText}>{studentsText}</span>

        {stats.length > 0 && (
          <div className={styles.stats}>
            {stats.map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <span className={styles.statDivider} aria-hidden="true" />}
                <div className={styles.stat}>
                  <span className={styles.statLabel}>{s.label}</span>
                  <span className={styles.statValue}>{s.value}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Breakdown row: two labeled amounts */}
      {breakdown && (
        <>
          <div className={styles.breakdownRow}>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>{breakdown.left.label}</span>
              <span className={styles.breakdownAmount}>{breakdown.left.amount}</span>
            </div>
            <div className={`${styles.breakdownItem} ${styles.breakdownItemRight}`}>
              <span className={styles.breakdownLabel}>{breakdown.right.label}</span>
              <span className={styles.breakdownAmount}>{breakdown.right.amount}</span>
            </div>
          </div>

          {/* Two-segment progress bar */}
          <div className={styles.progressBar}>
            <div
              className={styles.progressLeft}
              style={{ width: `${clamp(breakdown.left.percent)}%` }}
              title={`${breakdown.left.label}: ${breakdown.left.percent}%`}
            />
            <div
              className={styles.progressRight}
              style={{ width: `${clamp(breakdown.right.percent)}%` }}
              title={`${breakdown.right.label}: ${breakdown.right.percent}%`}
            />
          </div>

          <div className={styles.progressPercents}>
            <span>{breakdown.left.percent}%</span>
            <span>{breakdown.right.percent}%</span>
          </div>
        </>
      )}
    </div>
  );
};

/* Keep percent widths safe (backend rounding sometimes gives 100.5). */
const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

/* =============================================================
 * SchoolFeesTrend
 *
 * Renders a header + a horizontal row of fee status cards.
 * Pass any number of cards — component grid adapts.
 *
 * Props:
 *   - title       : string (falls back to translations.labels.schoolFeesTrend)
 *   - icon        : ReactNode
 *   - cards       : array (see FeeStatusCard for shape)
 *   - loading     : bool
 *   - error       : string | null
 *   - emptyMessage: string
 * ============================================================= */
const SchoolFeesTrend = ({
  title,
  icon = <FaCalendarAlt />,
  cards = [],
  loading = false,
  error = null,
  emptyMessage = "No fee data available",
}) => {
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  const headerTitle = title || t.schoolFeesTrend || "School Fees Trend";

  const renderBody = () => {
    if (loading) return <div className={styles.stateBox}>Loading…</div>;
    if (error)
      return (
        <div className={`${styles.stateBox} ${styles.stateError}`}>
          {String(error)}
        </div>
      );
    if (!Array.isArray(cards) || cards.length === 0)
      return <div className={styles.stateBox}>{emptyMessage}</div>;

    return (
      <div className={styles.cardGrid}>
        {cards.map((card) => (
          <FeeStatusCard key={card.id} card={card} />
        ))}
      </div>
    );
  };

  return (
    <div className="w-100">
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {icon && (
            <span className={styles.icon} aria-hidden="true">
              {icon}
            </span>
          )}
          <h3 className="chartTitle">{headerTitle}</h3>
        </div>
      </div>

      {renderBody()}
    </div>
  );
};

export default SchoolFeesTrend;
